import { useEffect, useState } from 'react';
import {
  Card, Form, Button, ButtonGroup, Alert, Row, Col, Modal, Table, Badge, Tab, Tabs,
} from 'react-bootstrap';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  reposService,
  learnedContextService,
  auditService,
  findingsService,
  exceptionsService, // attachException (write); the exception *read* is joined server-side
  ApiError,
} from '@/services/api';
import type { RepoPatch } from '@/services/api/repos-service';
import type {
  CoverageCounts,
  CoverageFilter,
  CveFinding,
  FindingException,
  FindingRequest,
} from '@/common/types';
import { useAsync } from '@/common/hooks/use-async';
import { AsyncBoundary } from '@/common/components/async-boundary';
import { useAuth } from '@/common/hooks/use-auth';
import {
  StackPicker,
  type SelectedStack,
  regionFromStackArn,
  stackNameFromArn,
} from '@/common/components/stack-picker';
import { EmailChipsInput } from '@/common/components/email-chips-input';

/** Deep-linkable tabs via ?tab=… — keeps the repo page from being one long scroll. */
const TABS = ['config', 'findings', 'learned', 'activity'] as const;
type TabKey = (typeof TABS)[number];

/** A labeled group of related settings — gives the form clear context boundaries. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border rounded p-3 mb-4">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">{title}</legend>
      {children}
    </fieldset>
  );
}

/** Split a typed resource key "ecr:name" / "lambda:fn" into [type, id]; legacy bare -> ecr. */
function parseResourceKey(key: string): [string, string] {
  const i = key.indexOf(':');
  return i < 0 ? ['ecr', key] : [key.slice(0, i), key.slice(i + 1)];
}

const RESOURCE_TYPE_META: Record<string, { label: string; bg: string }> = {
  ecr: { label: 'ECR image', bg: 'primary' },
  lambda: { label: 'Lambda', bg: 'warning' },
};

/** Edits the repo's typed monitoredResources list: chips grouped by type + add/remove.
 *  Keys are "ecr:<name>" | "lambda:<fn>"; most are auto-discovered from the stacks. */
function MonitoredResourcesEditor({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [addType, setAddType] = useState<'ecr' | 'lambda'>('ecr');
  const [addId, setAddId] = useState('');

  const remove = (key: string) => onChange(value.filter((k) => k !== key));
  const add = () => {
    const id = addId.trim();
    if (!id) return;
    const key = `${addType}:${id}`;
    if (!value.includes(key)) onChange([...value, key]);
    setAddId('');
  };

  // Group for display.
  const groups: Record<string, string[]> = {};
  for (const key of value) {
    const [t, id] = parseResourceKey(key);
    (groups[t] ??= []).push(id);
  }

  return (
    <div>
      {value.length === 0 && (
        <div className="text-muted small mb-2">
          None yet — select the app's CloudFormation stack(s) above and Save to auto-discover, or
          add one manually below.
        </div>
      )}
      {Object.entries(groups).map(([type, ids]) => {
        const meta = RESOURCE_TYPE_META[type] ?? { label: type, bg: 'secondary' };
        return (
          <div key={type} className="mb-2">
            <div className="text-uppercase text-muted small mb-1">{meta.label}</div>
            <div className="d-flex flex-wrap gap-2">
              {ids.map((id) => (
                <Badge key={id} bg={meta.bg} className="d-flex align-items-center gap-1 fw-normal">
                  {id}
                  <span
                    role="button"
                    aria-label={`remove ${id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => remove(`${type}:${id}`)}
                  >
                    ×
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        );
      })}
      <Row className="g-2 mt-1 align-items-center">
        <Col xs="auto">
          <Form.Select
            size="sm"
            value={addType}
            onChange={(e) => setAddType(e.target.value as 'ecr' | 'lambda')}
          >
            <option value="ecr">ECR image</option>
            <option value="lambda">Lambda</option>
          </Form.Select>
        </Col>
        <Col xs="auto">
          <Form.Control
            size="sm"
            placeholder={addType === 'ecr' ? 'repository name' : 'function name'}
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
        </Col>
        <Col xs="auto">
          <Button size="sm" variant="outline-secondary" onClick={add} disabled={!addId.trim()}>
            Add
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default function RepoDetail() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const repo = params.get('repo') ?? '';
  const { actor } = useAuth();
  const { data, loading, error, reload } = useAsync(() => reposService.getRepo(repo), [repo]);

  const tabParam = params.get('tab');
  const activeTab: TabKey = (TABS as readonly string[]).includes(tabParam ?? '')
    ? (tabParam as TabKey)
    : 'config';
  // Deep-link the tab via ?tab=… (preserve ?repo=) so tabs are shareable and back/forward works.
  const selectTab = (key: string | null) => {
    const next = new URLSearchParams(params);
    next.set('tab', key ?? 'config');
    setParams(next, { replace: true });
  };

  const [form, setForm] = useState<RepoPatch>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showOffboard, setShowOffboard] = useState(false);

  // Adapt the form's ARN string[] <-> the StackPicker's SelectedStack[] view. The ARN list
  // on the form stays the source of truth (that's what we PATCH); name/region are derived
  // for display from each ARN.
  const selectedStacks: SelectedStack[] = (form.cloudformationStackArns ?? []).map((arn) => ({
    stackArn: arn,
    stackName: stackNameFromArn(arn),
    region: regionFromStackArn(arn),
  }));
  const setSelectedStacks: React.Dispatch<React.SetStateAction<SelectedStack[]>> = (action) => {
    setForm((f) => {
      const current: SelectedStack[] = (f.cloudformationStackArns ?? []).map((arn) => ({
        stackArn: arn,
        stackName: stackNameFromArn(arn),
        region: regionFromStackArn(arn),
      }));
      const next = typeof action === 'function' ? (action as (p: SelectedStack[]) => SelectedStack[])(current) : action;
      return { ...f, cloudformationStackArns: next.map((s) => s.stackArn) };
    });
  };

  useEffect(() => {
    if (data?.repo) {
      const r = data.repo;
      setForm({
        enabled: Boolean(r.enabled),
        policy: (r.policy as string) ?? 'assist',
        defaultBranch: (r.defaultBranch as string) ?? '',
        // Typed monitored resources; fall back to legacy ecrRepoNames (bare names -> ecr:<name>).
        monitoredResources:
          (r.monitoredResources as string[]) ??
          ((r.ecrRepoNames as string[]) ?? []).map((n) => `ecr:${n}`),
        owners: r.owners ?? [],
        branchPattern: r.branchPattern ?? '',
        exceptionBranchPattern: r.exceptionBranchPattern ?? '',
        ciEnabled: Boolean(r.ciEnabled),
        requireValidation: Boolean(r.requireValidation),
        aiContextRoot: (r.aiContextRoot as string) ?? 'ai_context',
        cloudformationStackArns: (r.cloudformationStackArns as string[]) ?? [],
      });
    }
  }, [data]);

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await reposService.patchRepo(repo, actor, form);
      setMsg({ ok: true, text: 'Saved' });
      reload();
    } catch (e) {
      const t = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setMsg({ ok: false, text: `Save failed — ${t}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Card.Header>
        <span>Repo: {repo}</span>
      </Card.Header>
      <Card.Body>
        <AsyncBoundary loading={loading} error={error}>
          {data && (
            <Tabs activeKey={activeTab} onSelect={selectTab} className="mb-3">
              {/* --- Configuration --- */}
              <Tab eventKey="config" title="Configuration">
                <Form onSubmit={(e) => e.preventDefault()}>
                  {msg && <Alert variant={msg.ok ? 'success' : 'danger'}>{msg.text}</Alert>}

                  {/* --- Status --- */}
                  <Section title="Status">
                    <Form.Check
                      type="switch"
                      label="Enabled"
                      checked={Boolean(form.enabled)}
                      onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    />
                    <Form.Text className="text-muted d-block">
                      When on, vuln-sentinel acts on Inspector findings for this repo and opens
                      draft fix PRs a human reviews before merge (it never merges on its own). Turn
                      off to pause all remediation while keeping the repo onboarded.
                    </Form.Text>
                  </Section>

                  {/* --- Monitored resources & discovery --- */}
                  <Section title="Monitored resources">
                    <p className="text-muted">
                      Browse a region, filter, and select the CloudFormation stack(s) that make up
                      this app. vuln-sentinel reads these stacks (now and daily) and auto-discovers
                      the Inspector-scannable resources in them (ECR images and Lambda functions) —
                      so adding a resource to a stack is picked up without re-onboarding. Saving
                      re-runs discovery immediately.
                    </p>
                    <Form.Group className="mb-3">
                      <Form.Label>CloudFormation stacks</Form.Label>
                      <StackPicker selected={selectedStacks} setSelected={setSelectedStacks} />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Monitored resources</Form.Label>
                      <MonitoredResourcesEditor
                        value={form.monitoredResources ?? []}
                        onChange={(monitoredResources) => setForm({ ...form, monitoredResources })}
                      />
                      <Form.Text className="text-muted">
                        The Inspector-scannable resources vuln-sentinel watches for this repo —
                        ECR images and Lambda functions. Normally <strong>auto-populated</strong>{' '}
                        from the selected stacks (that's what links a scanned resource to its
                        source). Add one manually only for a resource not in a stack you can point
                        at; discovered resources are merged in and manual ones are kept.
                      </Form.Text>
                    </Form.Group>
                  </Section>

                  {/* --- Branches --- */}
                  <Section title="Branches">
                    <Form.Group className="mb-3">
                      <Form.Label>Default branch</Form.Label>
                      <Form.Control
                        value={form.defaultBranch ?? ''}
                        placeholder="e.g. main, sandbox, develop"
                        onChange={(e) => setForm({ ...form, defaultBranch: e.target.value })}
                      />
                      <Form.Text className="text-muted">
                        The branch fixes branch off and PRs target — this repo's integration branch
                        for this environment.
                      </Form.Text>
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Fix branch pattern</Form.Label>
                          <Form.Control
                            value={form.branchPattern ?? ''}
                            onChange={(e) => setForm({ ...form, branchPattern: e.target.value })}
                          />
                          <Form.Text className="text-muted">
                            Branch name for the repo's fix PR. <code>{'{type}'}</code> ={' '}
                            <code>fix</code>, <code>{'{groupKey}'}</code> = <code>all</code> (one
                            PR per repo covers all vulnerabilities), <code>{'{date}'}</code> = UTC{' '}
                            <code>YYYYMMDD</code>. e.g. <code>vuln-sentinel/fix-all-20261104</code>.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Exception branch pattern</Form.Label>
                          <Form.Control
                            value={form.exceptionBranchPattern ?? ''}
                            onChange={(e) => setForm({ ...form, exceptionBranchPattern: e.target.value })}
                          />
                          <Form.Text className="text-muted">
                            Name for documentation-only exception PRs — set a prefix your CI skips.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Section>

                  {/* --- AI context --- */}
                  <Section title="AI context">
                    <Form.Group>
                      <Form.Label>AI context root</Form.Label>
                      <Form.Control
                        value={form.aiContextRoot ?? ''}
                        placeholder="ai_context"
                        onChange={(e) => setForm({ ...form, aiContextRoot: e.target.value })}
                      />
                      <Form.Text className="text-muted">
                        Where this repo keeps its security AI context, relative to the repo root
                        (default <code>ai_context</code>). The agent clones the repo and reads
                        markdown under this folder (the <code>container-vulnerabilities</code>
                        subfolder underneath is still auto-detected). Set it if your team keeps
                        context elsewhere, e.g. <code>docs/security</code> — no repo change needed
                        to onboard.
                      </Form.Text>
                    </Form.Group>
                  </Section>

                  {/* --- Notifications --- */}
                  <Section title="Notifications">
                    <Form.Group>
                      <Form.Label>Owners</Form.Label>
                      <EmailChipsInput
                        value={form.owners ?? []}
                        onChange={(owners) => setForm({ ...form, owners })}
                        placeholder="dev@genesys.com — press Enter to add"
                      />
                      <Form.Text className="text-muted">
                        The people responsible for this repo — and who get notified when
                        vuln-sentinel opens a fix PR or drafts an exception. Add a distribution list
                        to alert a channel instead of individuals.
                      </Form.Text>
                    </Form.Group>
                  </Section>

                  <Button type="button" onClick={save} disabled={busy}>Save</Button>

                  {/* --- Danger zone (pinned at the bottom of config, settings-page convention) --- */}
                  <fieldset className="border border-danger rounded p-3 mt-5">
                    <legend className="float-none w-auto px-2 fs-6 text-uppercase text-danger">
                      Danger zone
                    </legend>
                    <p className="mb-2">
                      <strong>Offboard this repo.</strong> This permanently deletes vuln-sentinel's
                      configuration for <code>{repo}</code> — its image mappings, branch settings,
                      owners, AI-context root, and selected stacks — and stops ingesting its
                      Inspector findings. <strong>This cannot be undone.</strong> To monitor it again
                      you'd have to onboard and reconfigure it from scratch.
                    </p>
                    <p className="text-muted small mb-2">
                      Just want to pause temporarily? Turn off <strong>Enabled</strong> above
                      instead — that keeps all configuration and resumes instantly when switched
                      back on.
                    </p>
                    <Button type="button" variant="outline-danger" onClick={() => setShowOffboard(true)} disabled={busy}>
                      Offboard repo…
                    </Button>
                  </fieldset>
                </Form>
              </Tab>

              {/* --- Findings --- */}
              <Tab eventKey="findings" title="Findings">
                <FindingsSection repo={repo} actor={actor} />
              </Tab>

              {/* --- Learned context (M4 Phase 2) --- */}
              <Tab eventKey="learned" title="Learned context">
                <LearnedContextSection repo={repo} actor={actor} />
              </Tab>

              {/* --- Activity --- */}
              <Tab eventKey="activity" title="Activity">
                <ActivitySection repo={repo} />
              </Tab>
            </Tabs>
          )}
        </AsyncBoundary>
      </Card.Body>

      <OffboardModal
        show={showOffboard}
        repo={repo}
        busy={busy}
        onClose={() => setShowOffboard(false)}
        onConfirm={async () => {
          setBusy(true);
          setMsg(null);
          try {
            await reposService.offboardRepo(repo, actor);
            navigate('/repos', { replace: true });
          } catch (e) {
            const t = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
            setMsg({ ok: false, text: `Offboard failed — ${t}` });
            setShowOffboard(false);
          } finally {
            setBusy(false);
          }
        }}
      />
    </Card>
  );
}

/** Destructive-offboard confirmation: requires typing the exact repo to enable the button. */
function OffboardModal({
  show,
  repo,
  busy,
  onClose,
  onConfirm,
}: {
  show: boolean;
  repo: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const matches = confirmText.trim() === repo;
  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="text-danger">Offboard {repo}?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          This <strong>permanently deletes</strong> all vuln-sentinel configuration for this repo
          and stops ingesting its findings. It <strong>cannot be undone</strong> — re-adding it
          later means onboarding and reconfiguring from scratch.
        </p>
        <Form.Group>
          <Form.Label>
            Type <code>{repo}</code> to confirm:
          </Form.Label>
          <Form.Control
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={repo}
            autoFocus
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="danger" onClick={onConfirm} disabled={!matches || busy}>
          {busy ? 'Offboarding…' : 'Offboard repo'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

type FindingsView = 'cve' | 'req';

const COVERAGE_OPTIONS: { key: CoverageFilter; label: string }[] = [
  { key: 'needs-action', label: 'Needs action' },
  { key: 'covered', label: 'Covered by exception' },
  { key: 'all', label: 'All' },
];

/** A repo's Inspector findings, one row per distinct CVE (or, in the By REQ view, one row
 *  per filed ServiceNow REQ), with the covering exception + REQ and an aging/expiry badge.
 *  Hosts the on-demand "Pull latest findings" backfill button. */
function FindingsSection({ repo, actor }: { repo: string; actor: string }) {
  const [view, setView] = useState<FindingsView>('cve');
  // Default to what is still pending a decision (no valid exception) - the same set the
  // repos-list counts, so the two numbers agree.
  const [coverage, setCoverage] = useState<CoverageFilter>('needs-action');
  const [image, setImage] = useState('__all__');
  // Grouping (one entry per distinct CVE), image + coverage filtering, the exception join
  // and the REQ view all happen server-side; this component renders what GET /findings
  // returns and refetches when a filter changes. Switching By CVE / By REQ does not refetch:
  // one response carries both.
  const findings = useAsync(
    () =>
      findingsService.listFindings({
        repo,
        image: image === '__all__' ? undefined : image,
        coverage,
      }),
    [repo, image, coverage],
  );
  const [pulling, setPulling] = useState(false);
  const [pullMsg, setPullMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // The CVE whose "Attach existing REQ" modal is open (null = closed).
  const [attachTarget, setAttachTarget] = useState<CveFinding | null>(null);
  // The covered CVE whose REQ is being removed (undo of Attach); null = closed.
  const [removeTarget, setRemoveTarget] = useState<CveFinding | null>(null);
  const [attachMsg, setAttachMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // The REQ whose number is being edited in the By REQ view (null = closed).
  const [editTarget, setEditTarget] = useState<FindingRequest | null>(null);
  // The REQ being renewed (replaced by a newly filed one) / retired in the By REQ view.
  const [renewTarget, setRenewTarget] = useState<FindingRequest | null>(null);
  const [retireTarget, setRetireTarget] = useState<FindingRequest | null>(null);

  async function pull() {
    setPulling(true);
    setPullMsg(null);
    try {
      const res = await reposService.pullFindings(repo, actor);
      const text = res.note
        ? `Nothing to pull — ${res.note}.`
        : `Queued ${res.pulled} finding${res.pulled === 1 ? '' : 's'} for processing. ` +
          'They flow through the normal pipeline — grouped PRs appear shortly (duplicates are ignored).' +
          (res.resolved
            ? ` ${res.resolved} finding${res.resolved === 1 ? '' : 's'} Inspector no longer reports ${
                res.resolved === 1 ? 'was' : 'were'
              } marked resolved.`
            : '');
      setPullMsg({ ok: true, text });
      // Give the pipeline a moment, then refresh the list.
      setTimeout(() => findings.reload(), 1500);
    } catch (e) {
      const t = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setPullMsg({ ok: false, text: `Pull failed — ${t}` });
    } finally {
      setPulling(false);
    }
  }

  const groups = findings.data?.items ?? [];
  const requests = findings.data?.requests ?? [];
  const counts = findings.data?.coverageCounts;
  // A repo has a stable, small set of images (ember = ember-sandbox + ember-sandbox-schedule).
  // The server always returns the full list so the selector doesn't shrink once one is
  // chosen. Whether two images' vulns are "the same vuln" is a call only the dev team can
  // make, so the UI exposes the per-image view instead of assuming it.
  const images = findings.data?.images ?? [];
  const countFor = (key: CoverageFilter): number | undefined =>
    key === 'needs-action' ? counts?.needsAction : key === 'covered' ? counts?.covered : counts?.total;

  // The staleness rule comes from the server so the UI states and applies the numbers it
  // enforces; the fallbacks only cover a UI deployed a moment before its API.
  const expiryDays = findings.data?.expiryDays ?? 90;
  const warnDays = findings.data?.warnDays ?? 14;
  const expiredReqs = requests.filter((r) => r.status === 'expired').length;
  const fullyClearedReqs = requests.filter((r) => r.cveCount > 0 && r.presentCount === 0).length;
  const scope = image === '__all__' && images.length > 1 ? ' across all images' : '';

  return (
    <fieldset className="border rounded p-3">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">Findings</legend>
      <div className="d-flex justify-content-between align-items-start mb-2 gap-3">
        <p className="text-muted small mb-0">
          AWS Inspector findings for this repo's monitored resources. vuln-sentinel does{' '}
          <strong>not poll on a schedule</strong>: findings arrive when Inspector emits them (after
          it scans a deploy or a new image), so after pushing a fixed image the old CVEs stay
          listed until Inspector has scanned the new build and reported it. The repos-list totals
          are recalculated every 15 minutes on top of that. To refresh <em>this</em> repo right
          now (after onboarding, after a push, or when a pipeline is blocked by the very vulns it
          would scan) use <strong>Pull latest findings</strong>. It is per repo and safe to run
          anytime; duplicates are ignored.
        </p>
        <Button
          type="button"
          variant="outline-primary"
          size="sm"
          className="flex-shrink-0"
          onClick={pull}
          disabled={pulling}
        >
          {pulling ? 'Pulling…' : 'Pull latest findings'}
        </Button>
      </div>

      <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
        <ButtonGroup size="sm" aria-label="Group findings by">
          <Button
            type="button"
            variant={view === 'cve' ? 'primary' : 'outline-primary'}
            onClick={() => setView('cve')}
          >
            By CVE
          </Button>
          <Button
            type="button"
            variant={view === 'req' ? 'primary' : 'outline-primary'}
            onClick={() => setView('req')}
          >
            By REQ
          </Button>
        </ButtonGroup>
        {images.length > 1 && (
          <Form.Select
            size="sm"
            value={image}
            onChange={(e) => setImage(e.target.value)}
            style={{ width: 'auto' }}
            aria-label="Filter by image"
          >
            <option value="__all__">All images ({images.length})</option>
            {images.map((img) => (
              <option key={img} value={img}>
                {img}
              </option>
            ))}
          </Form.Select>
        )}
        {view === 'cve' && (
          <ButtonGroup size="sm" aria-label="Filter by exception coverage">
            {COVERAGE_OPTIONS.map((o) => {
              const n = countFor(o.key);
              return (
                <Button
                  key={o.key}
                  type="button"
                  variant={coverage === o.key ? 'secondary' : 'outline-secondary'}
                  onClick={() => setCoverage(o.key)}
                >
                  {o.label}
                  {n !== undefined && ` (${n})`}
                </Button>
              );
            })}
          </ButtonGroup>
        )}
      </div>
      <div className="text-muted small mb-2">
        {view === 'cve' ? (
          <>
            {groups.length} distinct {groups.length === 1 ? 'CVE' : 'CVEs'}
            {scope}
            {coverage !== 'covered' && !!counts?.expired && (
              <span className="text-danger">
                {' '}
                · {counts.expired} with an expired exception — re-evaluate
              </span>
            )}
          </>
        ) : (
          <>
            {requests.length} filed {requests.length === 1 ? 'REQ' : 'REQs'}
            {scope}
            {expiredReqs > 0 && <span className="text-danger"> · {expiredReqs} expired</span>}
            {fullyClearedReqs > 0 && (
              <span> · {fullyClearedReqs} with every CVE cleared (nothing left on the image)</span>
            )}
          </>
        )}
      </div>

      <StalenessNote expiryDays={expiryDays} warnDays={warnDays} />

      {pullMsg && (
        <Alert
          variant={pullMsg.ok ? 'success' : 'danger'}
          className="py-1 px-2 small"
          dismissible
          onClose={() => setPullMsg(null)}
        >
          {pullMsg.text}
        </Alert>
      )}
      {attachMsg && (
        <Alert
          variant={attachMsg.ok ? 'success' : 'danger'}
          className="py-1 px-2 small"
          dismissible
          onClose={() => setAttachMsg(null)}
        >
          {attachMsg.text}
        </Alert>
      )}

      <AsyncBoundary loading={findings.loading} error={findings.error}>
        {view === 'cve' ? (
          <Table responsive size="sm" hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Severity</th>
                <th>CVE</th>
                <th>Package</th>
                <th>Images</th>
                <th>Exception / REQ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <CveGroupRow
                  key={g.cveId}
                  group={g}
                  warnDays={warnDays}
                  onAttach={() => setAttachTarget(g)}
                  onRemove={() => setRemoveTarget(g)}
                />
              ))}
              {!groups.length && (
                <tr>
                  <td colSpan={7} className="text-muted small">
                    {emptyCveMessage(coverage, counts)}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        ) : (
          <RequestsTable
            requests={requests}
            expiryDays={expiryDays}
            warnDays={warnDays}
            onEdit={setEditTarget}
            onRenew={setRenewTarget}
            onRetire={setRetireTarget}
          />
        )}
      </AsyncBoundary>

      <RenewReqModal
        repo={repo}
        actor={actor}
        request={renewTarget}
        expiryDays={expiryDays}
        onClose={() => setRenewTarget(null)}
        onDone={(text) => {
          setRenewTarget(null);
          setAttachMsg({ ok: true, text });
          findings.reload();
        }}
      />
      <RetireReqModal
        repo={repo}
        actor={actor}
        request={retireTarget}
        onClose={() => setRetireTarget(null)}
        onDone={(text) => {
          setRetireTarget(null);
          setAttachMsg({ ok: true, text });
          findings.reload();
        }}
      />

      <RemoveReqModal
        repo={repo}
        actor={actor}
        finding={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onDone={(text) => {
          setRemoveTarget(null);
          setAttachMsg({ ok: true, text });
          findings.reload();
        }}
      />

      <EditReqModal
        repo={repo}
        actor={actor}
        request={editTarget}
        onClose={() => setEditTarget(null)}
        onDone={(text) => {
          setEditTarget(null);
          setAttachMsg({ ok: true, text });
          findings.reload();
        }}
      />

      <AttachReqModal
        repo={repo}
        actor={actor}
        finding={attachTarget}
        onClose={() => setAttachTarget(null)}
        onDone={(text) => {
          setAttachTarget(null);
          setAttachMsg({ ok: true, text });
          // Refresh so the CVE shows its new REQ (the server joins the exception).
          setTimeout(() => findings.reload(), 1200);
        }}
      />
    </fieldset>
  );
}

/** States when an exception counts as stale, so nobody has to guess why a CVE with a REQ is
 *  back under "Needs action". The numbers come from the server, which enforces them. */
function StalenessNote({ expiryDays, warnDays }: { expiryDays: number; warnDays: number }) {
  const months = Math.round(expiryDays / 30);
  return (
    <div className="text-muted small border-start border-3 ps-2 mb-2">
      <strong>When is an exception stale?</strong> {expiryDays} days (about {months} months) after
      the date its REQ was <em>filed</em> in ServiceNow — counted from the filing date, not the
      approval date. After that it no longer covers the CVE: it shows as{' '}
      <Badge bg="danger">expired — re-evaluate</Badge> and returns to <strong>Needs action</strong>{' '}
      until the CVE is remediated or a new REQ is filed. It turns{' '}
      <Badge bg="warning">amber</Badge> in the last {warnDays} days. File the new request in
      ServiceNow, then use <strong>Renew</strong> on the REQ in the By REQ view.
    </div>
  );
}

/** Why the By-CVE table is empty, given the active coverage filter. */
function emptyCveMessage(coverage: CoverageFilter, counts?: CoverageCounts): string {
  if (!counts || counts.total === 0) {
    return 'No findings recorded yet. If this repo has a known backlog, click “Pull latest findings”.';
  }
  if (coverage === 'needs-action') {
    return `Nothing needs action — all ${counts.total} CVEs are covered by a valid exception.`;
  }
  if (coverage === 'covered') {
    return 'No CVEs are covered by a valid exception yet. Use “Attach REQ” on a CVE once its exception is filed.';
  }
  return 'No findings in this view.';
}

/** The REQ-centric view: one row per filed ServiceNow REQ, soonest-to-expire first, with the
 *  CVEs under it. A REQ is the unit renewed in ServiceNow (one ticket, one expiry, N CVEs). */
function RequestsTable({
  requests,
  expiryDays,
  warnDays,
  onEdit,
  onRenew,
  onRetire,
}: {
  requests: FindingRequest[];
  expiryDays: number;
  warnDays: number;
  onEdit: (r: FindingRequest) => void;
  onRenew: (r: FindingRequest) => void;
  onRetire: (r: FindingRequest) => void;
}) {
  return (
    <Table responsive size="sm" hover className="mb-0 align-middle">
      <thead>
        <tr>
          <th style={{ width: 32 }}></th>
          <th>REQ</th>
          <th>Filed</th>
          <th title={`An exception is stale ${expiryDays} days after the date its REQ was filed.`}>
            Re-evaluate by
          </th>
          <th>CVEs</th>
          <th>On the image now</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {requests.map((r) => (
          <RequestRow
            key={r.req}
            request={r}
            warnDays={warnDays}
            onEdit={() => onEdit(r)}
            onRenew={() => onRenew(r)}
            onRetire={() => onRetire(r)}
          />
        ))}
        {!requests.length && (
          <tr>
            <td colSpan={7} className="text-muted small">
              No REQs recorded for this repo. After filing an exception in ServiceNow, use “Attach
              REQ” on its CVEs (By CVE view) to track it here.
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  );
}

function RequestRow({
  request: r,
  warnDays,
  onEdit,
  onRenew,
  onRetire,
}: {
  request: FindingRequest;
  warnDays: number;
  onEdit: () => void;
  onRenew: () => void;
  onRetire: () => void;
}) {
  const [open, setOpen] = useState(false);
  const lapsed = r.status === 'expired';
  const expiringSoon = !lapsed && r.daysLeft != null && r.daysLeft <= warnDays;
  // Renew only makes sense while something is still on the image to carry across; if every
  // CVE has cleared there is nothing to cover, so the right action is Retire.
  const canRenew = (lapsed || expiringSoon) && r.presentCount > 0;
  // ServiceNow issues an RITM first and assigns the REQ later; an RITM here is a placeholder
  // that someone should replace with the real REQ number.
  const isRitm = /^RITM/i.test(r.req);
  return (
    <>
      <tr>
        <td>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            aria-label={open ? 'collapse' : 'expand'}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? '▾' : '▸'}
          </Button>
        </td>
        <td className="small fw-semibold">
          {r.req}
          {isRitm && (
            <Badge bg="warning" text="dark" className="ms-2 fw-normal">
              RITM — REQ # pending
            </Badge>
          )}
        </td>
        <td className="small">{r.filedDate ?? '-'}</td>
        <td className="small">
          {r.expiresAt ? new Date(r.expiresAt * 1000).toLocaleDateString() : '-'}{' '}
          <DaysLeftBadge days={r.daysLeft} warnDays={warnDays} />
        </td>
        <td className="small">
          {r.cveCount} {r.cveCount === 1 ? 'CVE' : 'CVEs'}
        </td>
        <td className="small">
          {r.presentCount > 0 && <Badge bg="primary">{r.presentCount} present</Badge>}{' '}
          {r.clearedCount > 0 && <Badge bg="success">{r.clearedCount} cleared</Badge>}
        </td>
        <td className="text-end text-nowrap">
          <ButtonGroup size="sm">
            {canRenew && (
              <Button type="button" variant={lapsed ? 'danger' : 'warning'} onClick={onRenew}>
                Renew…
              </Button>
            )}
            {/* Editing keeps the ORIGINAL filing date, so it cannot fix a lapsed REQ - that
                needs a new ticket (Renew). It is for an RITM->REQ swap or a typo. */}
            {!lapsed && (
              <Button
                type="button"
                variant={isRitm ? 'warning' : 'outline-secondary'}
                onClick={onEdit}
              >
                {isRitm ? 'Set REQ #' : 'Edit REQ #'}
              </Button>
            )}
            {r.presentCount === 0 && (
              <Button type="button" variant="outline-danger" onClick={onRetire}>
                Retire…
              </Button>
            )}
          </ButtonGroup>
        </td>
      </tr>
      {open && (
        <tr>
          <td></td>
          <td colSpan={6} className="bg-light">
            <Table size="sm" borderless className="mb-0">
              <thead>
                <tr className="text-muted small">
                  <th>CVE</th>
                  <th>Severity</th>
                  <th>On the image</th>
                  <th>Images</th>
                </tr>
              </thead>
              <tbody>
                {r.cves.map((c) => (
                  <tr key={c.cveId}>
                    <td className="small">{c.cveId}</td>
                    <td>
                      {c.severity ? (
                        <Badge bg={SEV_BADGE[c.severity] ?? 'light'}>{c.severity}</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {c.present ? (
                        <Badge bg="primary">present</Badge>
                      ) : (
                        <Badge bg="success">cleared</Badge>
                      )}
                    </td>
                    <td className="small text-muted">{c.images.join(', ') || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {r.clearedCount > 0 && (
              <div className="small text-muted mt-1">
                Cleared CVEs are no longer reported by Inspector for this image — they can be
                retired from your exception docs.
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/** Days until a REQ must be re-evaluated (90 days from filing); negative once lapsed. */
function DaysLeftBadge({ days, warnDays }: { days?: number | null; warnDays: number }) {
  if (days === null || days === undefined) return null;
  if (days <= 0) return <Badge bg="danger">expired {Math.abs(days)}d ago</Badge>;
  if (days <= warnDays) return <Badge bg="warning">{days}d left</Badge>;
  return (
    <Badge bg="light" text="dark">
      {days}d left
    </Badge>
  );
}

/** Modal to replace a stale REQ with a newly filed one. Not a rename: the CVEs still on the image
 *  move to the NEW ticket with its own filing date (so its 90-day clock starts fresh) and the old
 *  REQ is retired. CVEs that have cleared are deliberately not carried over. */
function RenewReqModal({
  repo,
  actor,
  request,
  expiryDays,
  onClose,
  onDone,
}: {
  repo: string;
  actor: string;
  request: FindingRequest | null;
  expiryDays: number;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const [next, setNext] = useState('');
  const [filedDate, setFiledDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setNext('');
    setFiledDate('');
    setErr(null);
  }, [request]);

  const trimmed = next.trim();
  const valid =
    /^[A-Za-z0-9_-]{3,40}$/.test(trimmed) &&
    trimmed !== request?.req &&
    /^\d{4}-\d{2}-\d{2}$/.test(filedDate.trim());

  async function submit() {
    if (!request) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await exceptionsService.renewRequest(actor, {
        repo,
        fromReq: request.req,
        toReq: trimmed,
        filedDate: filedDate.trim(),
      });
      onDone(
        `Renewed ${res.fromReq} → ${res.toReq}: ${res.renewed.length} CVE${res.renewed.length === 1 ? '' : 's'} ` +
          `now covered until ${new Date(res.expiresAt * 1000).toLocaleDateString()}. ` +
          `${res.fromReq} is retired.` +
          (res.clearedNotCarried.length
            ? ` ${res.clearedNotCarried.length} cleared CVE${res.clearedNotCarried.length === 1 ? ' was' : 's were'} not carried over.`
            : ''),
      );
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  const present = request?.presentCount ?? 0;
  const cleared = request?.clearedCount ?? 0;
  return (
    <Modal show={request !== null} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Renew {request?.req}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          File a <strong>new</strong> exception in ServiceNow for these CVEs, then record it here.
          vuln-sentinel will:
        </p>
        <ul className="small text-muted">
          <li>
            move the <strong>{present}</strong> CVE{present === 1 ? '' : 's'} still on the image to
            the new REQ, whose {expiryDays}-day window counts from the filing date you enter;
          </li>
          <li>
            retire <code>{request?.req}</code> (in vuln-sentinel only — ServiceNow is never
            changed);
          </li>
          {cleared > 0 && (
            <li>
              <strong>not</strong> carry over the {cleared} CVE{cleared === 1 ? '' : 's'} that
              {cleared === 1 ? ' has' : ' have'} cleared — {cleared === 1 ? 'it is' : 'they are'}{' '}
              retired with the old REQ.
            </li>
          )}
        </ul>
        {err && (
          <Alert variant="danger" className="py-1 px-2 small">
            {err}
          </Alert>
        )}
        <Form.Group className="mb-3">
          <Form.Label>New ServiceNow number</Form.Label>
          <Form.Control
            value={next}
            placeholder="e.g. REQ0123456 (or the RITM until the REQ is assigned)"
            onChange={(e) => setNext(e.target.value)}
            autoFocus
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Filed date</Form.Label>
          <Form.Control
            type="date"
            value={filedDate}
            onChange={(e) => setFiledDate(e.target.value)}
          />
          <Form.Text className="text-muted">
            The date the new request was filed in ServiceNow, not today's date unless that is when
            it was filed.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Renewing…' : 'Renew'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Modal to retire a REQ: vuln-sentinel stops treating it as cover and drops it from the list.
 *  ServiceNow is never touched. Says plainly when CVEs would lose their cover. */
function RetireReqModal({
  repo,
  actor,
  request,
  onClose,
  onDone,
}: {
  repo: string;
  actor: string;
  request: FindingRequest | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setErr(null);
  }, [request]);

  async function submit() {
    if (!request) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await exceptionsService.retireRequest(actor, {
        repo,
        req: request.req,
        reason: reason.trim() || undefined,
      });
      onDone(`Retired ${res.req} (${res.retired} CVE${res.retired === 1 ? '' : 's'}).`);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show={request !== null} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="text-danger">Retire {request?.req}?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          vuln-sentinel will stop treating <code>{request?.req}</code> as cover and remove it from
          this list. It changes nothing in ServiceNow.
        </p>
        <Alert variant="info" className="py-2 px-3 small">
          Every CVE under this REQ has cleared, so nothing loses cover.
        </Alert>
        {err && (
          <Alert variant="danger" className="py-1 px-2 small">
            {err}
          </Alert>
        )}
        <Form.Group>
          <Form.Label>Reason (optional)</Form.Label>
          <Form.Control
            value={reason}
            placeholder="e.g. all CVEs cleared on the latest build"
            onChange={(e) => setReason(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="danger" onClick={submit} disabled={busy}>
          {busy ? 'Retiring…' : 'Retire REQ'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Modal to take ONE covered CVE off its REQ (direct undo of "Attach REQ"). The REQ's other CVEs
 *  stay covered; ServiceNow is never touched. */
function RemoveReqModal({
  repo,
  actor,
  finding,
  onClose,
  onDone,
}: {
  repo: string;
  actor: string;
  finding: CveFinding | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const req = finding ? (finding.exception?.serviceNowReqNumber ?? finding.req) : undefined;

  useEffect(() => {
    setReason('');
    setErr(null);
  }, [finding]);

  async function submit() {
    if (!finding || !req) return;
    setBusy(true);
    setErr(null);
    try {
      await exceptionsService.retireRequest(actor, {
        repo,
        req,
        cveIds: [finding.cveId],
        reason: reason.trim() || undefined,
      });
      onDone(`Removed ${finding.cveId} from ${req}. It is back under Needs action.`);
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show={finding !== null} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className="text-danger">
          Remove {finding?.cveId} from {req}?
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Use this when the REQ was attached to the wrong CVE. <code>{finding?.cveId}</code> goes
          back to <strong>Needs action</strong>. Other CVEs on <code>{req}</code> stay covered, and
          nothing changes in ServiceNow.
        </p>
        {err && (
          <Alert variant="danger" className="py-1 px-2 small">
            {err}
          </Alert>
        )}
        <Form.Group>
          <Form.Label>Reason (optional)</Form.Label>
          <Form.Control
            value={reason}
            placeholder="e.g. attached to the wrong CVE"
            onChange={(e) => setReason(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="danger" onClick={submit} disabled={busy}>
          {busy ? 'Removing…' : 'Remove REQ'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Modal to change the ServiceNow number recorded on a REQ — typically replacing an RITM id with
 *  the REQ number once ServiceNow assigns it. Renames in place, so the filed date and the
 *  re-evaluation date are unchanged (it is the same ticket). */
function EditReqModal({
  repo,
  actor,
  request,
  onClose,
  onDone,
}: {
  repo: string;
  actor: string;
  request: FindingRequest | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Start blank each time a different REQ opens the modal.
  useEffect(() => {
    setNext('');
    setErr(null);
  }, [request]);

  const trimmed = next.trim();
  const valid = /^[A-Za-z0-9_-]{3,40}$/.test(trimmed) && trimmed !== request?.req;

  async function submit() {
    if (!request) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await exceptionsService.updateRequestNumber(actor, {
        repo,
        fromReq: request.req,
        toReq: trimmed,
      });
      onDone(
        `Updated ${res.fromReq} → ${res.toReq} on ${res.updated} CVE${res.updated === 1 ? '' : 's'}. ` +
          'The re-evaluation date is unchanged.',
      );
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show={request !== null} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Change the ServiceNow number</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Replace <code>{request?.req}</code> with the number ServiceNow assigned — for example the
          REQ number for a ticket you first recorded under its RITM id. All{' '}
          {request?.cveCount ?? 0} CVE{request?.cveCount === 1 ? '' : 's'} under it are updated. It
          is the same ticket, so the filed date and the re-evaluation date{' '}
          <strong>do not change</strong>.
        </p>
        {err && (
          <Alert variant="danger" className="py-1 px-2 small">
            {err}
          </Alert>
        )}
        <Form.Group>
          <Form.Label>New number</Form.Label>
          <Form.Control
            value={next}
            placeholder="e.g. REQ0123456"
            onChange={(e) => setNext(e.target.value)}
            autoFocus
          />
          <Form.Text className="text-muted">
            Letters, digits, <code>-</code> or <code>_</code>. A number already recorded for this
            repo is rejected, so two tickets are never merged by accident.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Updating…' : 'Update number'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Modal to attach an existing ServiceNow REQ to a finding's CVE (Layer 2). */
function AttachReqModal({
  repo,
  actor,
  finding,
  onClose,
  onDone,
}: {
  repo: string;
  actor: string;
  finding: CveFinding | null;
  onClose: () => void;
  onDone: (text: string) => void;
}) {
  const [req, setReq] = useState('');
  const [filedDate, setFiledDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset fields whenever a different finding opens the modal.
  useEffect(() => {
    setReq('');
    setFiledDate('');
    setErr(null);
  }, [finding]);

  const show = finding !== null;
  const valid = req.trim() !== '' && /^\d{4}-\d{2}-\d{2}$/.test(filedDate.trim());

  async function submit() {
    if (!finding) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await exceptionsService.attachException(actor, {
        repo,
        cveIds: [finding.cveId],
        reqNumber: req.trim(),
        filedDate: filedDate.trim(),
        findingArns: finding.findingArns,
        component: finding.component,
        groupKey: finding.groupKey,
      });
      onDone(
        `Attached ${res.reqNumber} to ${finding.cveId}. It's now suppressed until ` +
          `re-evaluation (${new Date(res.expiresAt * 1000).toLocaleDateString()}).`,
      );
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Attach existing ServiceNow exception</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="small text-muted">
          Record a ServiceNow exception your team already filed for{' '}
          <code>{finding?.cveId}</code>. vuln-sentinel will <strong>suppress</strong> this CVE (no
          fix PR) until its 90-day re-evaluation window — counted from the filing date you enter —
          lapses. vuln-sentinel never files the exception itself; this just records one that exists.
        </p>
        {err && <Alert variant="danger" className="py-1 px-2 small">{err}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>ServiceNow REQ number</Form.Label>
          <Form.Control
            value={req}
            placeholder="e.g. REQ0012345"
            onChange={(e) => setReq(e.target.value)}
            autoFocus
          />
          <Form.Text className="text-muted">
            Only have the RITM id so far? Enter that — you can replace it with the REQ number
            later from the By REQ view.
          </Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>Filed date</Form.Label>
          <Form.Control
            type="date"
            value={filedDate}
            onChange={(e) => setFiledDate(e.target.value)}
          />
          <Form.Text className="text-muted">
            The date the ServiceNow exception was filed. The 90-day re-evaluation window is counted
            from this date.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Attaching…' : 'Attach REQ'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const SEV_BADGE: Record<string, string> = {
  CRITICAL: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'secondary',
};

/** One distinct CVE (already grouped server-side) with an expandable per-occurrence view.
 *  Deliberately shows no pipeline "status": the findings table's status (IN_PROGRESS etc.)
 *  is internal bookkeeping - set when the coalescer hands a group to the agent and never
 *  reset when an exception is attached - so it contradicts the Exception / REQ column. */
function CveGroupRow({
  group,
  warnDays,
  onAttach,
  onRemove,
}: {
  group: CveFinding;
  warnDays: number;
  onAttach: () => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { req, exception } = group;
  // Attach (or re-attach a renewed REQ) whenever there is no VALID exception. Keyed on the
  // server's coverage, not the stored state: a REQ past its 90 days stays SUBMITTED until the
  // daily sweep flips it, but it no longer covers anything and must be renewable now.
  const canAttach = group.coverage !== 'covered';

  return (
    <>
      <tr>
        <td>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0 text-decoration-none"
            aria-label={open ? 'collapse' : 'expand'}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? '▾' : '▸'}
          </Button>
        </td>
        <td>
          <Badge bg={SEV_BADGE[group.severity] ?? 'light'}>{group.severity}</Badge>
        </td>
        <td className="small">{group.cveId}</td>
        <td className="small">{group.packageName || '-'}</td>
        <td className="small text-muted">{group.images.join(', ') || '-'}</td>
        <td className="small">
          {req ? (
            <span>
              {req} <ExceptionAging exception={exception} warnDays={warnDays} />
            </span>
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td className="text-end">
          {canAttach ? (
            <Button type="button" size="sm" variant="outline-secondary" onClick={onAttach}>
              Attach REQ
            </Button>
          ) : (
            <Button type="button" size="sm" variant="outline-danger" onClick={onRemove}>
              Remove REQ
            </Button>
          )}
        </td>
      </tr>
      {open && (
        <tr>
          <td></td>
          <td colSpan={6} className="bg-light">
            <div className="small mb-1 text-muted">
              {group.occurrences.length} Inspector finding
              {group.occurrences.length === 1 ? '' : 's'}:
            </div>
            <Table size="sm" borderless className="mb-0">
              <thead>
                <tr className="text-muted small">
                  <th>Image</th>
                  <th>Image digest</th>
                </tr>
              </thead>
              <tbody>
                {group.occurrences.map((o) => (
                  <tr key={o.findingArn}>
                    <td className="small font-monospace">{o.image || '-'}</td>
                    <td className="small font-monospace text-muted">{shortDigest(o.imageDigest)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </td>
        </tr>
      )}
    </>
  );
}

/** First 12 hex chars of a sha256 image digest (Lambda findings have none). */
function shortDigest(digest?: string): string {
  const d = String(digest ?? '');
  return d.startsWith('sha256:') ? d.slice(7, 19) : '-';
}

/** Aging/expiry badge derived from an exception's expiresAt vs now (90-day re-eval). */
function ExceptionAging({
  exception,
  warnDays,
}: {
  exception?: FindingException | null;
  warnDays: number;
}) {
  if (!exception?.expiresAt) return null;
  const now = Math.floor(Date.now() / 1000);
  const days = Math.round((exception.expiresAt - now) / 86400);
  if (days <= 0) return <Badge bg="danger">expired — re-evaluate</Badge>;
  if (days <= warnDays) return <Badge bg="warning">expires in {days}d</Badge>;
  return <Badge bg="light" text="dark">expires in {days}d</Badge>;
}

/** vuln-sentinel's own learned lessons for this repo (loadContext layer 3), with retire. */
function LearnedContextSection({ repo, actor }: { repo: string; actor: string }) {
  const { data, loading, error, reload } = useAsync(
    () => learnedContextService.listLearnedContext(repo),
    [repo],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function retire(entryId: string) {
    setBusy(entryId);
    setMsg(null);
    try {
      await learnedContextService.retireLearnedContext(repo, entryId, actor);
      reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? `${e.status}: ${e.message}` : String(e));
    } finally {
      setBusy(null);
    }
  }

  const items = data?.items ?? [];
  return (
    <fieldset className="border rounded p-3">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">Learned context</legend>
      <p className="text-muted small">
        Lessons vuln-sentinel distilled from feedback on this repo. The agent reloads the
        scope-relevant ones on future runs (loadContext layer 3). Retire a lesson to stop loading it.
      </p>
      {msg && <Alert variant="danger" className="py-1 px-2 small">{msg}</Alert>}
      {loading && <div className="text-muted small">Loading…</div>}
      {error && <Alert variant="danger" className="py-1 px-2 small mb-0">{error}</Alert>}
      {!loading && !error && (
        <Table responsive size="sm" className="mb-0">
          <thead>
            <tr><th>Scope</th><th>Lesson</th><th>Conf.</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.entryId}>
                <td><Badge bg="info">{l.scope}</Badge></td>
                <td>{l.content}</td>
                <td className="small text-muted">{l.confidence ?? '-'}</td>
                <td>
                  <Button
                    type="button" size="sm" variant="outline-secondary"
                    disabled={busy === l.entryId}
                    onClick={() => retire(l.entryId)}
                  >
                    {busy === l.entryId ? '…' : 'Retire'}
                  </Button>
                </td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={4} className="text-muted small">No learned lessons yet.</td></tr>
            )}
          </tbody>
        </Table>
      )}
    </fieldset>
  );
}

/** Recent audit activity for this repo (newest-first), embedded from the audit feed. */
function ActivitySection({ repo }: { repo: string }) {
  const { data, loading, error } = useAsync(
    () => auditService.listAudit({ repo, limit: 15 }),
    [repo],
  );
  const items = data?.items ?? [];
  return (
    <fieldset className="border rounded p-3">
      <legend className="float-none w-auto px-2 fs-6 text-uppercase text-muted">
        Recent activity
      </legend>
      {loading && <div className="text-muted small">Loading…</div>}
      {error && <Alert variant="danger" className="py-1 px-2 small mb-0">{error}</Alert>}
      {!loading && !error && (
        <Table responsive size="sm" className="mb-0">
          <thead><tr><th>When</th><th>Action</th><th>By</th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.auditId}>
                <td className="small">{a.timestamp ? new Date(a.timestamp).toLocaleString() : '-'}</td>
                <td><Badge bg="secondary">{a.action}</Badge></td>
                <td className="small">{a.actor}</td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={3} className="text-muted small">No activity yet.</td></tr>
            )}
          </tbody>
        </Table>
      )}
      <Link to={`/audit?repo=${encodeURIComponent(repo)}`} className="small">Full activity →</Link>
    </fieldset>
  );
}
